import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header.js';
import Sidebar from './components/Sidebar.js';
import WelcomeScreen from './components/Welcome.js';
import ProjectView from './components/ProjectView.js';
import NewProjectDialog from './components/NewProjectDialog.js';
import OnboardingDialog from './components/OnboardingDialog.js';
import MLConsentDialog from './components/MLConsentDialog.js';
import FeedbackDialog from './components/FeedbackDialog.js';
import TosAcceptanceDialog, { CURRENT_TOS_VERSION } from './components/TosAcceptanceDialog.js';
import { createProject, updateProjectName, deleteProject, updateUserProfile, db, auth } from './firebase';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { withRetry, toUserMessage } from './utils/retry';
import { trackEvent } from './analytics';
import { captureDoor, spendDoorOnProjectCreated } from './doorAttribution';
import { normalizeTab, DEFAULT_STUDIO_TAB } from './utils/studioTabs';
import useFeatureFlag from './hooks/useFeatureFlag';

// Dismissing the ML-training consent dialog records NOTHING (issue #398), so
// the user stays un-consented and the gate would otherwise re-open the dialog
// every time this component re-runs its profile check — on a Studio remount
// (back from /account) or on any new auth-state emission. This session-scoped
// marker is what makes a dismissal mean "not now" for the rest of the visit
// while still re-asking on a later visit. sessionStorage (not localStorage) on
// purpose: the ask must come back in a new browser session, since a dismissal
// is not a decision and we still have no lawful basis without one.
const ML_CONSENT_DISMISSED_KEY_PREFIX = 'tweedia:mlConsentDismissed:';

// Fallback marker store for browsers where sessionStorage is missing or throws
// (Safari private mode, storage-blocked embeds, quota exceeded). Without it the
// marker would silently never stick there and the dialog would re-open on every
// Studio remount — the nag loop this marker exists to prevent. Module scope, so
// it lives exactly as long as the page: a reload is a new session either way.
const dismissedThisPageLoad = new Set();

function safeSessionStorage() {
  try {
    if (typeof sessionStorage === 'undefined' || sessionStorage === null) return null;
    return sessionStorage;
  } catch (_err) {
    return null;
  }
}

function mlConsentAskedThisSession(uid) {
  if (!uid) return false;
  if (dismissedThisPageLoad.has(uid)) return true;
  const s = safeSessionStorage();
  if (!s) return false;
  try {
    return s.getItem(`${ML_CONSENT_DISMISSED_KEY_PREFIX}${uid}`) === '1';
  } catch (_err) {
    return false;
  }
}

function markMlConsentAskedThisSession(uid) {
  if (!uid) return;
  const s = safeSessionStorage();
  if (s) {
    try {
      s.setItem(`${ML_CONSENT_DISMISSED_KEY_PREFIX}${uid}`, '1');
      return;
    } catch (_err) { /* quota / blocked write — fall through to memory */ }
  }
  dismissedThisPageLoad.add(uid);
}

function Studio({ user, signIn, showSignInDialog, setShowSignInDialog, handleDialogClose, credits, creditsLoading }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId: routeProjectId, tab: routeTab } = useParams();
  const selectedProject = routeProjectId || null;
  const activeTab = normalizeTab(routeTab) || DEFAULT_STUDIO_TAB;
  const { enabled: containerV2 } = useFeatureFlag('projectContainerV2', { defaultValue: true });
  const [projects, setProjects] = useState([]);
  // First snapshot arrival guard — we only reconcile the URL against the
  // real project list after the initial Firestore snapshot lands. Prevents
  // a flicker/redirect loop where an unknown-looking id gets replaced
  // before we've even loaded the user's projects.
  const hasLoadedProjectsRef = useRef(false);
  // A just-created project's id is navigated to before the projects
  // snapshot includes it. Without this ref the reconcile effect would see
  // an "unknown" id and bounce the user back to the first project.
  const pendingProjectIdRef = useRef(null);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [showTosAcceptance, setShowTosAcceptance] = useState(false);
  const [tosError, setTosError] = useState(null);
  const [tosSubmitting, setTosSubmitting] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showMLConsent, setShowMLConsent] = useState(false);
  // Cached gate-relevant subset of the user profile (tosVersion,
  // onboardingCompleted, ml_training_consent). Populated on first sign-in
  // check so subsequent gate transitions can be computed without an extra
  // Firestore read per dialog dismissal.
  const [profileGateData, setProfileGateData] = useState(null);
  const [projectError, setProjectError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // A door landing page deep-links an ALREADY signed-in visitor straight here
  // as /studio?door=..., skipping the sign-in step where attribution would
  // otherwise be stashed. Capturing on mount is what keeps that path in the
  // funnel; captureDoor allowlists the value, so an arbitrary ?door= is dropped.
  useEffect(() => {
    captureDoor(window.location.search);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('lock-scroll', drawerOpen);
    return () => {
      document.body.classList.remove('lock-scroll');
    };
  }, [drawerOpen]);

  // Pure gate-decision helper. Given the relevant subset of user profile
  // fields, surfaces the next dialog to show. Centralized so the post-
  // dialog transition handlers can re-run the same logic against locally
  // mutated state without an extra Firestore read.
  const advanceGate = (data) => {
    if (data.tosVersion !== CURRENT_TOS_VERSION) {
      setShowTosAcceptance(true);
    } else if (!data.onboardingCompleted) {
      setShowOnboarding(true);
    } else if (!data.ml_training_consent && !mlConsentAskedThisSession(user?.uid)) {
      // No stored record means NO consent (never "default on"). Ask again on
      // each new visit until the user makes an explicit choice — but only once
      // per session, so a dismissal is not nagged inside the same visit.
      setShowMLConsent(true);
    }
  };

  useEffect(() => {
    if (!user) {
      setShowTosAcceptance(false);
      setTosError(null);
      setShowOnboarding(false);
      setShowMLConsent(false);
      setProfileGateData(null);
      return;
    }

    let isMounted = true;
    const checkTosOnboardingAndConsent = async () => {
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        const data = userDoc.exists() ? userDoc.data() : {};
        if (isMounted) {
          setProfileGateData(data);
          advanceGate(data);
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      }
    };
    checkTosOnboardingAndConsent();

    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleTosAccept = async () => {
    if (!user) {
      setShowTosAcceptance(false);
      return;
    }
    // The server rejects any version other than the one it serves, so the
    // write is a real failure mode, not a formality. Keep the dialog up
    // until it succeeds: dismissing first would drop the user into the
    // Studio ungated with the failure invisible, and report an acceptance
    // to analytics that was never recorded.
    setTosSubmitting(true);
    setTosError(null);
    try {
      await updateUserProfile({
        tos_acceptance: { version: CURRENT_TOS_VERSION },
      });
    } catch (error) {
      console.error('Error saving ToS acceptance:', error);
      setTosError(
        error?.message ||
          'We could not record your acceptance. Please try again.'
      );
      setTosSubmitting(false);
      return;
    }
    setTosSubmitting(false);
    setShowTosAcceptance(false);
    trackEvent('tos_accepted', {});
    // Re-run the gate logic against the cached profile with ToS
    // marked as accepted. Avoids a redundant Firestore read since
    // updateUserProfile only mutates ToS fields and onboarding/ML
    // consent state was already fetched in the initial check.
    const updated = {
      ...(profileGateData || {}),
      tosVersion: CURRENT_TOS_VERSION,
    };
    setProfileGateData(updated);
    advanceGate(updated);
  };

  const handleTosDecline = async () => {
    setShowTosAcceptance(false);
    setTosError(null);
    // User declined — sign them out
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out after ToS decline:', error);
    }
  };

  const handleOnboardingComplete = async () => {
    trackEvent('onboarding_completed', {});
    if (user) {
      try {
        await updateUserProfile({ onboardingCompleted: true });
        // Show ML consent dialog after onboarding for informational purposes
        setShowMLConsent(true);
      } catch (error) {
        console.error('Error saving onboarding status:', error);
      }
    }
    setShowOnboarding(false);
  };

  const handleMLConsentResponse = async (consented) => {
    setShowMLConsent(false);
    markMlConsentAskedThisSession(user?.uid);
    trackEvent('ml_consent_updated', { granted: !!consented });
    if (user) {
      try {
        await updateUserProfile({
          ml_training_consent: { consented, version: '1.0' },
        });
      } catch (error) {
        console.error('Error saving ML consent:', error);
      }
    }
  };

  // Dismissal (Escape / overlay click) must record NOTHING: GDPR consent
  // requires a clear affirmative action, so inactivity cannot grant it (#398).
  const handleMLConsentDismiss = () => {
    setShowMLConsent(false);
    markMlConsentAskedThisSession(user?.uid);
    trackEvent('ml_consent_dismissed', {});
  };

  const sortProjects = (projects) => {
    return projects.sort((a, b) => b.createdAt - a.createdAt);
  };

  const handleCreateProject = () => {
    if (!user) {
      setShowSignInDialog(true);
    } else {
      setShowNewProjectDialog(true);
    }
  };

  const handleSaveNewProject = async (projectName) => {
    setIsCreatingProject(true);
    setProjectError(null);
    try {
      const result = await withRetry(() => createProject({ projectName }));
      const newProjectId = result.data.newProjectId;
      trackEvent('project_created', {
        project_id: newProjectId,
        ...spendDoorOnProjectCreated(),
      });
      pendingProjectIdRef.current = newProjectId;
      navigate(`/studio/${newProjectId}`);
      setShowNewProjectDialog(false);
    } catch (error) {
      console.error("Error creating project:", error);
      setProjectError(toUserMessage(error, "We couldn't create that project. Please try again."));
    } finally {
      setIsCreatingProject(false);
    }
  };

  const handleCancelNewProject = () => {
    setShowNewProjectDialog(false);
  };

  const handleUpdateProjectName = async (projectId, newName) => {
    setProjectError(null);
    try {
      await withRetry(() => updateProjectName({ projectId, newName }));
    } catch (error) {
      console.error("Error updating project name:", error);
      setProjectError(toUserMessage(error, "We couldn't rename that project. Please try again."));
    }
  };

  const handleDeleteProject = async (projectId) => {
    setProjectError(null);
    try {
      await withRetry(() => deleteProject({ projectId }));
      // The onSnapshot listener will handle updating the project list
      // and selecting a new project if the deleted one was selected.
    } catch (error) {
      console.error("Error deleting project:", error);
      setProjectError(toUserMessage(error, "We couldn't delete that project. Please try again."));
    }
  };

  useEffect(() => {
    if (user) {
      const q = query(collection(db, "projects"), where("userId", "==", user.uid));
      const unsubscribe = onSnapshot(
        q,
        (querySnapshot) => {
          const projectsData = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            projectsData.push({ ...data, id: doc.id, createdAt: data.createdAt.toDate() });
          });
          const sortedProjects = sortProjects(projectsData);
          setProjects(sortedProjects);
          hasLoadedProjectsRef.current = true;
        },
        (error) => {
          console.error('Error loading projects:', error);
          setProjectError(toUserMessage(error, "We couldn't load your projects. Please reload the page."));
        }
      );
      return () => unsubscribe();
    } else {
      setProjects([]);
      hasLoadedProjectsRef.current = false;
      pendingProjectIdRef.current = null;
    }
  }, [user]);

  // Reconcile the URL against the loaded project list. Only runs after the
  // first snapshot arrives so we don't redirect before we know what the
  // user actually has. Handles: no id + has projects → first project;
  // unknown id → first project (or /studio if none); invalid tab → drop
  // it back to the project's base URL.
  useEffect(() => {
    if (!hasLoadedProjectsRef.current) return;
    const isPendingCreate = routeProjectId && routeProjectId === pendingProjectIdRef.current;
    if (projects.length === 0) {
      if (routeProjectId && !isPendingCreate) navigate('/studio', { replace: true });
      return;
    }
    const exists = routeProjectId && projects.some(p => p.id === routeProjectId);
    if (exists && isPendingCreate) {
      pendingProjectIdRef.current = null;
    }
    if (!routeProjectId || !exists) {
      if (!isPendingCreate) navigate(`/studio/${projects[0].id}`, { replace: true });
      return;
    }
    if (routeTab && normalizeTab(routeTab) === null) {
      navigate(`/studio/${routeProjectId}`, { replace: true });
    }
  }, [projects, routeProjectId, routeTab, navigate]);

  const project = projects.find(p => p.id === selectedProject);

  return (
    <div className="App">
      <Header signIn={signIn} user={user} credits={credits} creditsLoading={creditsLoading} onToggleDrawer={() => setDrawerOpen(prev => !prev)} drawerOpen={drawerOpen} />
      {projectError && (
        <div className="studio-error-banner" role="alert">
          <span>{projectError}</span>
          <button
            type="button"
            className="studio-error-dismiss"
            onClick={() => setProjectError(null)}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}
      <div className="App-body">
        {drawerOpen && <div className="drawer-overlay" onClick={() => setDrawerOpen(false)} />}
        <Sidebar
          projects={projects}
          onProjectSelected={(id) => { navigate(`/studio/${id}`); setDrawerOpen(false); }}
          createProject={handleCreateProject}
          isCreatingProject={isCreatingProject}
          selectedProject={selectedProject}
          user={user}
          drawerOpen={drawerOpen}
          condensed={containerV2}
          onFeedbackClick={() => { setShowFeedback(true); setDrawerOpen(false); }}
        />
        <main className="main-content">
          {
            selectedProject == null || !project ? (
              <div className="welcome-content">
                <WelcomeScreen createProject={handleCreateProject} />
              </div>
            ) : (
              <ProjectView
                saveName={handleUpdateProjectName}
                project={project}
                onDelete={handleDeleteProject}
                credits={credits}
                activeTab={activeTab}
                onTabChange={(tab) => navigate(`/studio/${project.id}/${tab}`)}
                containerV2={containerV2}
              />
            )
          }
        </main>
        {showNewProjectDialog && <NewProjectDialog onSave={handleSaveNewProject} onCancel={handleCancelNewProject} isCreating={isCreatingProject} />}
        {showTosAcceptance && <TosAcceptanceDialog onAccept={handleTosAccept} onDecline={handleTosDecline} error={tosError} submitting={tosSubmitting} />}
        {showOnboarding && <OnboardingDialog onComplete={handleOnboardingComplete} />}
        {showMLConsent && <MLConsentDialog onAccept={() => handleMLConsentResponse(true)} onDecline={() => handleMLConsentResponse(false)} onDismiss={handleMLConsentDismiss} />}
        {showFeedback && (
          <FeedbackDialog
            onClose={() => setShowFeedback(false)}
            page={location.pathname}
            projectId={routeProjectId || null}
          />
        )}
      </div>
    </div>
  );
}

export default Studio;